import React from "react";
import { Link } from "react-router-dom";
import { lookupBrainType } from "../data/brainTypes";
import { Brain, ChartLineUp, Info, Hourglass } from "@phosphor-icons/react";
import CalendlyEmbed from "./CalendlyEmbed";
import BrainTypeProbabilities, { formatBrainTypeLabel } from "./BrainTypeProbabilities";

/**
 * BrainTypeView — three rendering states:
 *
 *  A. brain_type set (validated by work coach) → full Neurogram model + Dutch
 *     sections + (optional) Calendly block.
 *  B. survey result exists but brain_type NOT yet set → "voorlopige uitslag"
 *     screen with the top brain type, probability chart and a notice that
 *     the work coach still needs to validate. Calendly block included for
 *     isOwn views so the coachee can plan their validation session.
 *  C. nothing yet (no brain_type, no result) → either a CTA to start the
 *     online test (isOwn + survey_enabled) or an informational message.
 */
export default function BrainTypeView({ coachee, surveyResult, isOwn = false, calendlyEmbed = "" }) {
  if (!coachee) return null;
  const brainType = lookupBrainType(coachee.brain_type);
  const surveyEnabled = coachee.survey_enabled !== false;
  const hasPreliminaryResult = !!surveyResult;

  // Calendly booking widget is only relevant while the breintype is still
  // PRELIMINARY (test submitted but not yet validated by the work coach).
  // Once the brain type is definitief, the conversation has already taken
  // place — showing the embed again would be confusing.
  const showCalendly = isOwn && !!calendlyEmbed && !brainType && hasPreliminaryResult;
  const calendlyBlock = showCalendly ? (
    <section className="neuro-card p-6 md:p-8" data-testid="brain-type-calendly">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Volgende stap</div>
      <h3 className="font-display text-2xl font-bold tracking-tight mt-1">
        Plan een gesprek met je work coach
      </h3>
      <p className="text-sm text-zinc-600 mt-2">
        Bespreek je uitslag persoonlijk en krijg de definitieve interpretatie van je breintype.
      </p>
      <div className="mt-6">
        <CalendlyEmbed html={calendlyEmbed} />
      </div>
    </section>
  ) : null;

  // ---- State A: validated brain type ----
  if (brainType) {
    const titleParts = [`Breintype #${brainType.number}`];
    if (brainType.code) titleParts.push(`(${brainType.code})`);
    if (brainType.name_nl) titleParts.push(`— ${brainType.name_nl}`);
    const title = titleParts.join(" ");
    return (
      <div className="space-y-10 max-w-4xl" data-testid="brain-type-view">
        <div className="neuro-card-bold p-6 bg-white">
          <div className="font-display text-2xl font-bold tracking-tight mb-4 text-center">
            {title}
          </div>
          {brainType.image_url ? (
            <img
              src={brainType.image_url}
              alt={title}
              className="block mx-auto w-full max-w-2xl h-auto"
              data-testid="brain-type-image"
            />
          ) : (
            <div className="text-center text-zinc-500 py-12" data-testid="brain-type-image-missing">
              Afbeelding van het Neurogram-model wordt nog toegevoegd.
            </div>
          )}
        </div>

        {brainType.sections && brainType.sections.length > 0 ? (
          <BrainTypeSections sections={brainType.sections} />
        ) : (
          <div className="neuro-card p-6 text-sm text-zinc-600" data-testid="brain-type-text-missing">
            <div className="font-display text-lg font-bold tracking-tight mb-1">Toelichting volgt</div>
            De Nederlandse toelichting voor dit breintype wordt binnenkort toegevoegd.
          </div>
        )}
        {calendlyBlock}
      </div>
    );
  }

  // ---- State B: preliminary result, awaiting validation ----
  if (hasPreliminaryResult) {
    const hasProbs = surveyResult.probabilities && Object.keys(surveyResult.probabilities).length > 0;
    return (
      <div className="space-y-10 max-w-4xl" data-testid="brain-type-preliminary">
        <div className="neuro-card-bold p-8 bg-white">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm shrink-0">
              <Hourglass size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                Voorlopige uitslag
              </div>
              <h3 className="font-display text-2xl font-bold tracking-tight">
                Je breintype-test is afgerond
              </h3>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Dit is een <span className="font-semibold">voorlopige</span> uitslag op basis van je antwoorden.
                Je work coach beoordeelt de uitslag in een gesprek met je en stelt daarna jouw definitieve
                breintype vast. Zodra dat gebeurd is, verschijnt hier de volledige toelichting met
                bijbehorende afbeelding en eigenschappen.
              </p>

              {hasProbs && surveyResult.top_brain_type_id && (
                <div className="mt-6 inline-flex items-center gap-3 border-l-4 border-[#1E88E5] bg-[#1E88E5]/5 px-4 py-3 rounded-r-sm" data-testid="preliminary-top-brain-type">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Meest waarschijnlijk</span>
                  <span className="font-display text-base font-bold tracking-tight">
                    #{surveyResult.top_brain_type_id} {formatBrainTypeLabel(surveyResult.top_brain_type_id)}
                  </span>
                </div>
              )}
              {surveyResult.submitted_at && (
                <div className="mt-3 text-xs text-zinc-400">
                  Ingevuld op {new Date(surveyResult.submitted_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {hasProbs && (
          <BrainTypeProbabilities
            probabilities={surveyResult.probabilities}
            topId={surveyResult.top_brain_type_id}
          />
        )}

        {calendlyBlock}
      </div>
    );
  }

  // ---- State C: nothing yet ----
  return (
    <div className="space-y-10 max-w-4xl">
      <div className="neuro-card-bold p-10 max-w-3xl bg-white" data-testid="brain-type-pending">
        <div className="flex items-start gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center bg-zinc-100 rounded-sm shrink-0">
            <Brain size={24} weight="duotone" color="#1E88E5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl font-bold tracking-tight">
              Breintype nog niet bepaald
            </h3>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              Het Neurogram-model wordt pas getoond zodra jouw breintype is vastgesteld. Dat kan via
              de online breintype-test of door je work coach handmatig op basis van een gesprek.
            </p>

            {isOwn && surveyEnabled && (
              <div className="mt-6">
                <Link
                  to="/survey"
                  data-testid="brain-type-start-survey"
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
                >
                  <ChartLineUp size={16} /> Start de breintype-test
                </Link>
                <p className="text-xs text-zinc-500 mt-3 max-w-md">
                  De uitslag van de test is altijd voorlopig. De definitieve vaststelling van jouw
                  breintype wordt door de work coach gedaan na een dubbelcheck.
                </p>
              </div>
            )}

            {!surveyEnabled && (
              <div className="mt-6 text-sm text-zinc-500 inline-flex items-center gap-2" data-testid="survey-disabled-notice">
                <Info size={16} className="text-zinc-400" />
                De online breintype-test is niet beschikbaar voor jouw account. Het breintype wordt
                via een 1-op-1 gesprek met je work coach bepaald.
              </div>
            )}
          </div>
        </div>
      </div>
      {calendlyBlock}
    </div>
  );
}

function BrainTypeSections({ sections }) {
  return (
    <div className="space-y-8" data-testid="brain-type-sections">
      {sections.map((s, i) => (
        <section key={s.heading || `sec-${i}`} className="neuro-card p-6" data-testid={`brain-section-${i}`}>
          {s.heading && (
            <h3 className="font-display text-xl font-bold tracking-tight mb-3">{s.heading}</h3>
          )}
          {s.body && (
            <div className="text-[15px] leading-relaxed text-zinc-700 whitespace-pre-line">
              {s.body}
            </div>
          )}
          {s.table && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm border border-zinc-200">
                {s.table.headers && (
                  <thead className="bg-zinc-50">
                    <tr>
                      {s.table.headers.map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-medium border-b border-zinc-200">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {s.table.rows.map((row, ri) => (
                    <tr key={`r${ri}-${String(row[0] || "").slice(0, 24)}`} className="border-b border-zinc-100">
                      {row.map((cell, ci) => (
                        <td key={`${ri}-${ci}`} className="px-3 py-2 align-top">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {s.list && (
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[15px] text-zinc-700">
              {s.list.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-[#1E88E5] mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
